/**
 * Picking the interface language.
 *
 * Rendered twice: in the header for a signed-in user, and on the sign-in screen
 * — that second one matters, because somebody who cannot read the English form
 * cannot get far enough to find a setting behind it.
 *
 * Each language is named **in itself** ("Čeština", never "Czech"). A list that
 * names languages in the language you cannot currently read is a list you cannot
 * use to escape.
 */
import { useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import TranslateIcon from '@mui/icons-material/Translate';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../i18n/useLanguage';
import type { LanguageCode } from '../i18n/config';

export interface LanguageSelectorProps {
  /** Matches the neighbouring icon buttons. */
  size?: 'small' | 'medium';
}

export function LanguageSelector({ size = 'medium' }: LanguageSelectorProps) {
  const { t } = useTranslation('common');
  const { language, languages, setLanguage } = useLanguage();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  function choose(code: LanguageCode) {
    setLanguage(code);
    setAnchor(null);
  }

  return (
    <>
      <Tooltip title={t('language.change')}>
        <IconButton
          size={size}
          onClick={(event) => setAnchor(event.currentTarget)}
          aria-label={t('language.change')}
          aria-haspopup="menu"
        >
          <TranslateIcon fontSize={size === 'small' ? 'small' : 'medium'} />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 180 } } }}
      >
        {languages.map((entry) => (
          <MenuItem
            key={entry.code}
            selected={entry.code === language}
            onClick={() => choose(entry.code)}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Box component="span" aria-hidden sx={{ fontSize: 18, lineHeight: 1 }}>
                {entry.flag}
              </Box>
            </ListItemIcon>
            {/* Never translated — see the note at the top of this file. */}
            <ListItemText>{entry.label}</ListItemText>
            {entry.code === language && <CheckIcon fontSize="small" sx={{ ml: 1.5 }} />}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
